<template>
    <div
        class="assignment-show-header"
        v-if="assignmentIn">
        <b-alert
            class="alert-assignment-status"
            v-if="
                (assignmentIn.statuses.length > 0 && assignmentIn.statuses[0].hasActivities()) ||
                assignmentIn.hasStatus()
            "
            show
            variant="info">
            {{ getStatusText }}
        </b-alert>

        <div class="left-side">
            <h1 class="title">
                {{ getTitle }}
                <p
                    class="status-info"
                    v-if="SubAssignmentHasStatus()">
                    {{ getSubAssignmentStatusText }}
                </p>
            </h1>
            <div v-if="error">
                <b-alert
                    show
                    variant="danger">
                    {{ $t('assignment.no_collaboration_partner_selected') }}
                </b-alert>
            </div>
        </div>

        <div class="right-side">
            <!--            Rating badge            -->
            <b-badge
                class="badge-rating"
                v-if="hasRatingResult"
                :variant="'primary'"
                pill>
                <i class="fas fa-fw fa-trophy"></i>{{ assignmentIn.portfolio_rating.getValue() }}
            </b-badge>

            <!--            Project switcher            -->
            <design-2021-apprentice-multiselect-component
                class="project-switcher with-border"
                id="select-project"
                v-if="!$authUser.isStudent() && groupIn"
                v-model="selectedProject"
                :multiple-select="false"
                :options="groupIn.projects"
                label="title"
                track-by="id"
                @input="redirectToNewProject()">
            </design-2021-apprentice-multiselect-component>

            <!--            Feedback            -->
            <b-button
                class="btn-comment-exclamation btn-show-feedback-history"
                v-if="showFeedback"
                v-b-modal="'assignment-show-feedback-detail-modal'"
                v-b-tooltip.hover
                :title="$t('common.feedback')"
                variant="warning-icon">
            </b-button>

            <!--Slides-->
            <b-button
                class="btn-show-slides"
                v-b-tooltip="$t('lesson_manual.show_slides')"
                variant="primary"
                v-if="assignmentIn.lesson_id"
                @click="showSlides">
                {{ $t('lesson_manual.show_slides') }}
                <div class="icon">
                    <i class="fas fa-play"></i>
                </div>
            </b-button>

            <!--            Submit all sub-assignments            -->
            <b-button
                class="btn-upload btn-submit-all"
                v-if="canSubmit && assignmentIn.hasPartWithTypeAssignment()"
                :v-b-tooltip.hover.top="$t('Opdracht inleveren')"
                variant="success-icon"
                @click="onSubmit()">
            </b-button>

            <!--            Assignments partners           -->
            <b-button
                class="btn-users btn-assignment-partner"
                v-b-modal="'assignment-show-partner-modal'"
                v-if="
                    assignmentIn.assignment_cooperations.length &&
                    assignmentIn.assignment_cooperations[0].assignment_partners.length
                "
                :v-b-tooltip.hover.top="$t('assignment.collaborate')"
                variant="success-icon">
            </b-button>

            <!--            Assignments rubric(s)            -->
            <b-button
                class="btn-check-list btn-assignment-rubric"
                v-if="assignmentIn.assignment_rubric && assignmentIn.assignment_rubric.assignment_rubric_users.length"
                :v-b-tooltip.hover.top="$t('assignment.to_rubric')"
                variant="primary-icon"
                @click="toRubricUser()">
            </b-button>

            <!--            View choice            -->
            <b-button
                class="btn-page-view-toggle"
                v-b-tooltip.hover
                v-if="parseInt($groupType) !== 7"
                :class="[{ 'btn-book-open': viewType === 1 }, { 'btn-bars': viewType === 2 }]"
                :title="viewType === 1 ? 'Paginaweergave' : 'Lijstweergave'"
                variant="primary-icon"
                @click="changeViewType()">
            </b-button>

            <!--            Context menu            -->
            <b-dropdown
                class="ellipsis-nav assignments-nav"
                v-if="($authUser.isStudent() && canReopen) || !$authUser.isStudent()"
                no-caret
                right
                text="Right align"
                toggle-class="assignments-context-menu"
                variant="primary">
                <template #button-content>
                    <i class="fas fa-ellipsis-v fa-fw"></i>
                </template>

                <b-dropdown-item
                    class="btn-checked btn-approve-assignment text-success"
                    v-if="!$authUser.isStudent() && canClose"
                    @click="onClose">
                    {{ $t('assignment.approve_assignment_confirmation_title') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-redo btn-reopen-assignment"
                    v-if="canReopen"
                    @click="onReopen">
                    {{ $t('assignment.reopen_assignment_confirmation_title') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-medal btn-assignment-rating"
                    v-if="!$authUser.isStudent() && canRate"
                    v-b-modal="'portfolio-rating-assignment'">
                    {{ $t('assignment.assess_assignment') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-history btn-assignment-activities"
                    v-if="!$authUser.isStudent()"
                    v-b-modal="'assignment-show-activities'">
                    {{ $t('assignment.assignment_history') }}
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <!--            Modals            -->
        <design-2021-assignment-show-feedback-details-model-component
            v-if="showFeedback"
            :assignment-in="assignmentIn"
            :project-id="projectId">
        </design-2021-assignment-show-feedback-details-model-component>

        <design-2021-assignment-show-partner-modal-component
            v-if="
                assignmentIn.assignment_cooperations.length &&
                assignmentIn.assignment_cooperations[0].assignment_partners.length
            "
            :assignment-partner-in="assignmentIn.assignment_cooperations[0].assignment_partners[0]"
            :assignment-cooperation-in="assignmentIn.assignment_cooperations[0]"
            :group-id-in="groupIdIn">
        </design-2021-assignment-show-partner-modal-component>

        <design-2021-portfolio-show-rating-modal-component
            v-if="canRate"
            :children-in="this.assignmentIn.sub_assignments"
            :rating-in="this.assignmentIn.portfolio_rating"
            :title="$t('assignment.assignment_rating', { assignment: this.assignmentIn.name })"
            :child-title="$t('assignment.sub_assignment_ratings')"
            key-name="portfolio-rating-assignment"
            @save-rating="savePortfolioRating($event)">
        </design-2021-portfolio-show-rating-modal-component>

        <design-2021-assignment-show-logs-modal-component :activities-in="getActivitiesForModal()">
        </design-2021-assignment-show-logs-modal-component>

        <design-2021-assignment-skill-tree-skill-picker-component
            ref="rubric-skill-picker"
            @chosen-skill-ids="setSkillIds"></design-2021-assignment-skill-tree-skill-picker-component>
    </div>
</template>

<script>
    import { ords } from '../../../app';
    import moment from 'moment';
    import Design2021ApprenticeMultiselectComponent
        from '../../common/design-2021-apprentice-multiselect-component.vue';

    const subAssignmentStatusOrds = ords.use('subAssignmentStatus');

    export default {
        name: 'design-2021-assignment-show-title-buttons-component',
        components: { Design2021ApprenticeMultiselectComponent },
        props: [
            'assignmentIn',
            'viewType',
            'groupIdIn',
            'isPreview',
            'studentsIn',
            'projectIn',
            'groupIn',
            'selectedSubAssignmentId'
        ],

        data() {
            return {
                deadlinePopupTitle: this.$t('common.deadline'),
                bgColor: 'disabled-icon-only',
                error: false,
                selectedProject: this.projectIn,
                skill_ids: [],
                arUser: null
            };
        },
        mounted() {
            if (parseInt(this.$groupType) === 7) {
                this.$emit('updatedViewType', 1);
            }
        },

        computed: {
            showFeedback() {
                if (this.isPreview) {
                    return false;
                }

                return this.assignmentIn.hasFeedback() || !this.$authUser.isStudent();
            },
            projectId() {
                return !this.isPreview ? this.projectIn.id : null;
            },

            badgeColor() {
                if (!this.showBadge()) {
                    return '';
                }

                if (this.assignmentIn.getStatus().isInSubmittedState()) {
                    return 'warning';
                }
                if (this.assignmentIn.getStatus().isInReopenedState()) {
                    return 'danger';
                }
                if (this.assignmentIn.getStatus().isInClosedState()) {
                    return 'success';
                }
            },

            badgeLabel() {
                if (!this.showBadge()) {
                    return '';
                }

                if (this.assignmentIn.getStatus().isInSubmittedState()) {
                    return this.$t('assignment.submitted');
                }
                if (this.assignmentIn.getStatus().isInReopenedState()) {
                    return this.$t('assignment.reopened');
                }
                if (this.assignmentIn.getStatus().isInClosedState()) {
                    return this.$t('assignment.closed');
                }
            },

            canSubmit() {
                if (!this.projectId) {
                    return false;
                }

                return (
                    !this.$authUser.isTeacher() &&
                    (!this.assignmentIn.hasStatus() || this.assignmentIn.getStatus().isWaitingForSubmit())
                );
            },

            canClose() {
                if (!this.projectId) {
                    return false;
                }

                if (this.$authUser.isStudent() || !this.assignmentIn.hasStatus()) {
                    return false;
                }

                return this.assignmentIn.getStatus().isInSubmittedState();
            },

            canReopen() {
                if (!this.projectId) {
                    return false;
                }

                if (!this.assignmentIn.hasStatus()) {
                    return false;
                }

                if (this.$authUser.isStudent() && this.assignmentIn.getStatus().status !== 0) {
                    return false;
                }

                return (
                    this.assignmentIn.getStatus().isInSubmittedState() || this.assignmentIn.getStatus().isInClosedState()
                );
            },

            canFeedback() {
                if (!this.projectId) {
                    return false;
                }

                return !this.$authUser.isStudent();
            },

            hasRatingResult() {
                if (this.assignmentIn.portfolio_rating && this.assignmentIn.portfolio_rating.type) {
                    return this.assignmentIn.portfolio_rating.hasResult();
                }

                return false;
            },

            canRate() {
                // todo determine flow
                if (this.$authUser.isStudent()) {
                    return false;
                }
                return this.assignmentIn.portfolio_rating && this.assignmentIn.portfolio_rating.type;
            },

            getStatusText() {
                if (this.assignmentIn.statuses.length > 0 && this.assignmentIn.statuses[0].hasActivities()) {
                    const activities = this.assignmentIn.statuses[0].getActivities();

                    if (this.assignmentIn.statuses[0].status === 1) {
                        return this.$t('assignment.submitted_on', {
                            date: this.$options.filters.date_time_om(activities[0].created_at)
                        });
                    } else {
                        for (const activity of activities) {
                            if (activity.properties.attributes.status === 1) {
                                let date = activity.created_at;
                                return this.$t('assignment.last_time_submitted_on', {
                                    date: this.$options.filters.date_time_om(activity.created_at)
                                });
                            }
                        }
                    }
                } else if (this.assignmentIn.hasStatus()) {
                    //for the assignments that are older (doesn't have activities but does have status)
                    return this.$t('assignment.submitted_on', {
                        date: this.$options.filters.date_time_om(this.assignmentIn.statuses[0].created_at)
                    });
                }

                return null;
            },

            getTitle() {
                if (this.assignmentIn.sub_assignments.length === 0 || this.selectedSubAssignmentId === undefined) {
                    return null;
                }

                const subAssignment = this.assignmentIn.sub_assignments.filter(
                    (subAssignment) => subAssignment.id === this.selectedSubAssignmentId
                );

                return subAssignment[0].name;
            },

            getSubAssignmentStatusText() {
                let subAssignment = null;

                if (this.selectedSubAssignmentId) {
                    subAssignment = this.assignmentIn.sub_assignments
                        .filter((subAssignment) => subAssignment.id === this.selectedSubAssignmentId)
                        .shift();
                }

                if (subAssignment.hasStatus()) {
                    return `Deelopdracht ingeleverd op ${this.$options.filters.date_time_om(subAssignment.statuses[0].created_at)}`;
                }
            },
            unAnsweredParts() {
                let partsUnanswered = false;
                this.assignmentIn.sub_assignments.forEach((subAssignment) => {
                    subAssignment.pages.forEach((page) => {
                        page.parts.forEach((part) => {
                            if (!part.answers.length && part.type === 2) {
                                partsUnanswered = true;
                            }
                        });
                    });
                });

                return partsUnanswered;
            }
        },

        methods: {
            showSlides() {
                window.open(this.routes.lessonManualLessonShowSlides(this.assignmentIn.lesson_id));
            },
            getActivitiesForModal() {
                let activities = [];
                if (this.assignmentIn.hasStatus() && this.assignmentIn.getStatus().getActivities()) {
                    activities = this.assignmentIn.getStatus().getActivities();
                }
                return activities;
            },

            showBadge() {
                if (!this.assignmentIn.hasStatus()) {
                    return false;
                }

                return !this.assignmentIn.getStatus().isInPristineSate();
            },

            rubricCanSubmit() {
                if (this.$authUser.isStudent() && this.assignmentIn.assignment_rubrics.length) {
                    let arUser = null;
                    this.assignmentIn.assignment_rubrics.forEach((assignmentRubric) => {
                        arUser = assignmentRubric.assignment_rubric_users.find(
                            (arUser) => arUser.user_id === this.$authUser.id
                        );
                    });
                    if (arUser) {
                        this.arUser = arUser;
                        for (const rubricForm of arUser.rubric_forms) {
                            if (!rubricForm.rubric_form_results.length) {
                                return false;
                            }
                            let myResults = rubricForm.rubric_form_results.find(
                                (result) => result.user_id === this.$authUser.id
                            );
                            if (myResults === undefined) {
                                return false;
                            }
                        }
                        return true;
                    }
                    return false;
                } else if (this.$authUser.isStudent() && this.assignmentIn.assignment_skill_tree) {
                    return false;
                }
                return true;
            },

            onSubmit() {
                let submitText = this.$t('assignment.submit_assignment_confirmation_title');
                let okButtonText = this.$t('assignment.submit');

                if (this.unAnsweredParts) {
                    submitText = this.$t('assignment.confirm_submit_assignment');
                    okButtonText = this.$t('assignment.submit_anyway');
                }

                if (!this.rubricCanSubmit()) {
                    if (this.assignmentIn.assignment_skill_tree && !this.arUser) {
                        this.$refs['rubric-skill-picker'].show(this.assignmentIn);
                    } else if (this.assignmentIn.assignment_rubrics.length) {
                        this.alert
                            .confirm(
                                this.$t('assignment.submit_after_rubrics_confirmation_title'),
                                submitText + this.$t('assignment.submit_after_rubrics_confirmation_body'),
                                this.$t('assignment.submit_after_rubrics_confirmation_button')
                            )
                            .then((confirm) => {
                                if (confirm) {
                                    this.submit();
                                }
                            });
                    }
                } else {
                    this.alert
                        .confirm(this.$t('assignment.submit_assignment_confirmation_title'), submitText, okButtonText)
                        .then((confirm) => {
                            if (confirm) {
                                this.submit();
                            }
                        });
                }
            },
            setSkillIds(skillIds) {
                this.skill_ids = skillIds;
                this.submit();
            },

            submit() {
                this.alert.saving();
                const data = {
                    assignment_id: this.assignmentIn.id,
                    project_id: this.projectId
                };
                if (this.skill_ids.length) {
                    data.skill_ids = this.skill_ids;
                }

                this.axios.post(this.routes.assignmentStatusSubmit(), data).then(
                    (response) => {
                        if (!response.data.teacherSubmitOnAssignmentRubric) {
                            if (response.data.redirect) {
                                window.location.href = this.routes.assignmentRubricEdit(response.data.arUserId);
                                return;
                            }

                            if (this.$authUser.isStudent()) {
                                window.location.href = this.routes.portfolioShow(this.projectId);
                                return;
                            }
                            ords.hold();

                            // do assignment
                            if (!this.assignmentIn.hasStatus()) {
                                this.assignmentIn.addRelation('statuses', response.data.assignment);
                            } else {
                                this.assignmentIn.getStatus().update(response.data.assignment);

                                for (let activity of response.data.assignment.activities) {
                                    this.assignmentIn.getStatus().addRelation('activities', activity);
                                }
                            }

                            // do subAssignments
                            for (const subAssignment of this.assignmentIn.sub_assignments) {
                                let status = null;
                                for (const s of response.data.sub_assignments) {
                                    if (subAssignment.id === s.sub_assignment_id) {
                                        status = s;
                                        break;
                                    }
                                }

                                if (!status) {
                                    continue;
                                }

                                if (subAssignment.hasStatus()) {
                                    subAssignment.getStatus().update(status);
                                    for (let activity of status.activities) {
                                        ords.use('subAssignmentStatus').attach(status.id, 'activities', activity);
                                    }
                                } else {
                                    subAssignment.addRelation('statuses', status);
                                }
                            }

                            ords.continue();
                        }
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },

            toRubricUser() {
                let projectUserTeacher = this.projectIn.users.find((user) => user.id === this.$authUser.id);
                if (this.$authUser.isStudent()) {
                    let rubricUser = this.assignmentIn.assignment_rubric.assignment_rubric_users.find(
                        (rubricUser) => rubricUser.user_id === this.$authUser.id
                    );

                    window.location.href = this.routes.assignmentRubricEdit(rubricUser.id);
                } else if (this.$authUser.isTeacher() && projectUserTeacher) {
                    window.location.href = this.routes.assignmentRubricEdit(
                        this.assignmentIn.assignment_rubric.assignment_rubric_users[0].id
                    );
                } else {
                    window.location.href = this.routes.assignmentRubricShow(
                        this.assignmentIn.assignment_rubric.assignment_rubric_users[0].id
                    );
                }
            },

            onClose() {
                this.alert
                    .confirm(
                        this.$t('assignment.approve_assignment_confirmation_title'),
                        this.$t('assignment.approve_assignment_confirmation_body'),
                        this.$t('assignment.approve_assignment_confirmation_button')
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        this.alert.saving();
                        const data = {
                            assignment_id: this.assignmentIn.id,
                            project_id: this.projectId
                        };

                        this.axios.post(this.routes.assignmentStatusClose(), data).then(
                            (response) => {
                                ords.hold();

                                this.assignmentIn.getStatus().update(response.data.assignment);

                                for (let activity of response.data.assignment.activities) {
                                    this.assignmentIn.getStatus().addRelation('activities', activity);
                                }
                                for (const status of response.data.sub_assignments) {
                                    subAssignmentStatusOrds.update(status, status.id);
                                    for (let activity of status.activities) {
                                        ords.use('subAssignmentStatus').attach(status.id, 'activities', activity);
                                    }
                                }

                                ords.continue();

                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            }
                        );
                    });
            },

            onReopen() {
                this.alert
                    .confirm(
                        this.$t('assignment.reopen_assignment_confirmation_title'),
                        this.$t('assignment.reopen_assignment_confirmation_body'),
                        this.$t('assignment.reopen')
                    )
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving();
                            const data = {
                                assignment_id: this.assignmentIn.id,
                                project_id: this.projectId
                            };

                            this.axios.post(this.routes.assignmentStatusReopen(), data).then(
                                (response) => {
                                    ords.hold();

                                    this.assignmentIn.getStatus().update(response.data.assignment);

                                    for (let activity of response.data.assignment.activities) {
                                        this.assignmentIn.getStatus().addRelation('activities', activity);
                                    }

                                    for (const status of response.data.sub_assignments) {
                                        subAssignmentStatusOrds.update(status, status.id);

                                        for (let activity of status.activities) {
                                            ords.use('subAssignmentStatus').attach(status.id, 'activities', activity);
                                        }
                                    }
                                    ords.continue();

                                    this.alert.success();
                                },
                                (error) => {
                                    this.alert.ajaxError(error);
                                }
                            );
                        }
                    });
            },

            savePortfolioRating(values) {
                let promises = [];
                ords.hold();

                if (values.rating.value || values.rating.option_id || this.assignmentIn.portfolio_rating.hasResult()) {
                    this.alert.saving();

                    let rating_data = values.rating;

                    rating_data.project_id = this.projectId;

                    let promise = null;

                    if (!this.assignmentIn.portfolio_rating.hasResult()) {
                        promise = this.axios.post(this.routes.portfolioRatingResult(), rating_data).then(
                            (response) => {
                                this.assignmentIn.portfolio_rating.addRelation('results', response.data);
                                // this.alert.success();
                                // this.fireClose(); todo
                            },
                            (error) => {
                                if (error.response.data.error === 'assignmentpartner') {
                                    this.error = true;
                                } else {
                                    this.alert.ajaxError(error);
                                    this.error = false;
                                }
                            }
                        );
                    } else {
                        promise = this.axios.post(this.routes.portfolioRatingResultUpdate(), rating_data).then(
                            (response) => {
                                if (response.data) {
                                    this.assignmentIn.portfolio_rating.getResult().update(response.data);
                                } else {
                                    this.assignmentIn.portfolio_rating.getResult().remove();
                                }

                                // this.alert.success();
                                // if (!this.assignment.statuses || !this.assignment.statuses.length || this.assignment.statuses[0].status !== 1) {
                                //     this.fireClose();
                                // } else {
                                //     this.alert.success();
                                // } todo
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            }
                        );
                    }

                    if (promise) {
                        promises.push(promise);
                    }
                }

                if (promises.length) {
                    Promise.all(promises).then(() => {
                        ords.continue();
                        this.alert.success();
                    });
                } else {
                    ords.continue();
                }
            },

            changeViewType() {
                if (this.viewType === 1) {
                    this.$emit('updatedViewType', 2);
                }
                if (this.viewType === 2) {
                    this.$emit('updatedViewType', 1);
                }
            },
            showRatingsDetails() {
                this.$bvModal.show('assignment-show-ratings-detail-modal');
            },

            getDeadlineText() {
                const currentDate = moment().format('YYYY-MM-DD HH:mm:ss');
                const deadline = this.assignmentIn.deadlines[0].agenda_item.date_time;
                const daysLeft = moment(deadline).diff(moment(currentDate), 'days');

                this.bgColor =
                    parseInt(daysLeft) <= 7 ? 'danger-icon' : parseInt(daysLeft) <= 14 ? 'warning-icon' : 'disabled-icon';

                if (daysLeft > 0) {
                    return daysLeft > 14
                        ? this.$t('assignment.deadline_expires_in_days', { days: daysLeft })
                        : this.$t('assignment.deadline_almost_expires_in_days', { days: daysLeft });
                } else if (daysLeft === 0) {
                    const timeElapsed = moment.duration(moment(deadline).diff(moment(currentDate)));

                    if (parseInt(timeElapsed.asHours()) > 0 && parseInt(timeElapsed.asHours()) >= 0) {
                        return this.$t('assignment.deadline_almost_expires_in_hours_minutes', {
                            hours: parseInt(timeElapsed.asHours()),
                            minutes: parseInt(timeElapsed.asMinutes())
                        });
                    } else if (parseInt(timeElapsed.asMinutes()) > 0) {
                        return this.$t('assignment.deadline_almost_expires_in_minutes', {
                            minutes: parseInt(timeElapsed.asMinutes())
                        });
                    }
                } else {
                    this.deadlinePopupTitle = this.$t('assignment.deadline_passed');
                    return `Verlopen op: ${this.$options.filters.date_time_om(this.assignmentIn.deadlines[0].agenda_item.date_time)}`;
                }
            },

            redirectToNewProject() {
                window.location = this.routes.assignmentShow(this.assignmentIn.id, this.selectedProject.id);
            },

            SubAssignmentHasStatus() {
                let subAssignment = null;

                if (this.selectedSubAssignmentId) {
                    subAssignment = this.assignmentIn.sub_assignments
                        .filter((subAssignment) => subAssignment.id === this.selectedSubAssignmentId)
                        .shift();

                    return subAssignment.hasStatus();
                }

                return null;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .alert-assignment-status {
        flex: 1 0 100%;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .assignment-show-header {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        justify-content: flex-start;
        row-gap: map-get($grid-gutter-spacing, 'fifteen');
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

        .left-side,
        .right-side {
            flex: 1 0 100%;
        }

        .title {
            margin: 0;
        }

        .status-info {
            font-size: $base-font-size;
        }
    }

    .status-info {
        color: $text-muted-color;
        font-weight: $font-weight-medium;
        font-size: 1.125rem;
        align-self: center;
        flex: 1 0 100%;
        order: 20;
    }

    .right-side ::v-deep {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: map-get($grid-gutter-spacing, 'fifteen');

        .btn:not(.btn-show-slides) {
            width: 36px;
            min-height: 40px;
            height: auto;
            font-size: 1.125rem;
            align-items: center;
            padding: map-get($grid-gutter-spacing, 'eight');

            &:before,
            &:after {
                margin: 0;
            }
        }
    }

    .badge-rating {
        order: 1;
        padding: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'ten');
        font-size: $base-font-size;

        i {
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .project-switcher {
        order: 0;
        width: 100%;
    }

    .btn-show-feedback-history {
        order: 2;
        font-size: 1.375rem;
    }

    .btn-submit-all {
        order: 3;
    }

    .btn-page-view-toggle {
        order: 4;
    }

    .assignments-nav ::v-deep {
        order: 5;
        align-self: center;

        button.assignments-context-menu {
            background-color: $primary;
            color: $white !important;

            &:not(:disabled):not(.disabled):active,
            &:focus,
            &:focus-visible {
                background-color: $primary;
            }
        }

        .dropdown-menu {
            border: 1px solid $border-color;

            li {
                font-size: $default-font-size;

                &:not(.btn-approve-assignment):not(.btn-reopen-assignment) {
                    &:before {
                        color: $primary;
                    }
                }
            }

            .btn-approve-assignment {
                &:before {
                    color: $success;
                }
            }

            .btn-reopen-assignment {
                &:before {
                    color: $danger;
                }
            }
        }

        &.show {
            button.assignments-context-menu {
                background-color: $primary;
                color: $white !important;
            }
        }
    }

    .btn-show-slides {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: $default-font-size;
        font-weight: $font-weight-medium;
        column-gap: 18px;
        align-self: flex-end;
        order: 0;

        .icon {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            font-size: 0.75rem;
            width: 32px;
            height: 25px;
            top: 3px;

            i {
                z-index: 10;
                color: var(--primary);
            }

            &:before,
            &:after {
                content: '';
                position: absolute;
                display: block;
                background-color: $white;
                border-radius: $border-radius-sm;
                width: 33px;
                height: 23px;
            }

            &:before {
                left: -4px;
                top: -4px;
            }

            &:after {
                border: 2px solid var(--primary);
                border-radius: 6px;
            }
        }

        &:hover {
            .icon {
                i {
                    color: var(--primaryDarker);
                }
            }
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'sm')) {
        .project-switcher {
            width: 250px;
        }

        .badge-rating,
        .btn-show-feedback-history,
        .btn-submit-all,
        .btn-page-view-toggle,
        .assignments-nav ::v-deep {
            order: initial;
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'md')) {
        .assignment-show-header {
            column-gap: $grid-gutter-width;

            .left-side,
            .right-side {
                flex: 1 1 auto;
            }
        }

        .btn-show-slides {
            flex: 0 0 auto;
            align-self: flex-start;
            margin-left: auto;
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'xl')) {
        .assignment-show-header {
            row-gap: 0;
            column-gap: $grid-gutter-width;

            .left-side {
                align-self: center;
                flex: 0 1 45%;
            }

            .right-side {
                flex: 0 1 calc(55% - 30px);
                margin-left: auto;
            }
        }
    }
</style>
